import * as React from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import {
  CircularProgress,
  Menu,
  MenuItem,
  Checkbox,
  IconButton,
} from "@mui/material";
import { Eye } from "lucide-react";
import { NavLink } from "react-router-dom";
import { useEffect } from "react";
import { dummyOutstandingBalanceData } from "../../../../../dummyData";

const defaultColumns = [
  { id: "name", label: "Firm Name", minWidth: 150, required: true },
  { id: "address", label: "Address", minWidth: 200, required: false },
  { id: "phone", label: "Phone Number", minWidth: 120, required: true },
  { id: "lastPayment", label: "Last Payment", minWidth: 100, required: false },
  {
    id: "pendingAmount",
    label: "Pending Amount (₹)",
    minWidth: 140,
    required: true,
  },
  { id: "dueDate", label: "Due Date", minWidth: 100, required: false },
  { id: "status", label: "Status", minWidth: 80, required: true },
  { id: "action", label: "Action", minWidth: 80, required: true },
];

export default function AllTable({
  visibleColumns,
  onColumnToggle,
  setRefetchFn,
  searchQuery = "",
  filters = {},
}) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [isLoading, setIsLoading] = React.useState(false);
  const [filteredData, setFilteredData] = React.useState({
    firms: dummyOutstandingBalanceData,
    totalCount: dummyOutstandingBalanceData.length,
  });

  // Filter and sort data based on searchQuery and filters
  React.useEffect(() => {
    setIsLoading(true);

    setTimeout(() => {
      let filtered = [...dummyOutstandingBalanceData];

      // Apply search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(
          firm =>
            firm.name.toLowerCase().includes(query) ||
            firm.phone.includes(query) ||
            firm.address.toLowerCase().includes(query),
        );
      }

      // Apply status filter
      if (filters.status) {
        filtered = filtered.filter(
          firm => firm.status.toLowerCase() === filters.status.toLowerCase(),
        );
      }

      // Apply date range filter
      if (filters.fromDate || filters.toDate) {
        // This would be implemented with actual date parsing logic
        // For demo purposes, we'll just return the filtered data
      }

      // Apply sorting
      if (filters.sortBy) {
        const [field, direction] = filters.sortBy.split("_");

        filtered.sort((a, b) => {
          let valueA, valueB;

          if (field === "name") {
            valueA = a.name;
            valueB = b.name;
          } else if (field === "amount") {
            valueA = a.pendingAmount;
            valueB = b.pendingAmount;
          } else if (field === "date") {
            // Parse dates for proper comparison
            const parseDate = dateStr => {
              const [day, month, year] = dateStr.split("/");
              return new Date(`${year}-${month}-${day}`);
            };
            valueA = parseDate(a.dueDate);
            valueB = parseDate(b.dueDate);
          }

          if (direction === "asc") {
            return valueA > valueB ? 1 : -1;
          } else {
            return valueA < valueB ? 1 : -1;
          }
        });
      }

      setFilteredData({
        firms: filtered,
        totalCount: filtered.length,
      });
      setIsLoading(false);
    }, 600); // Simulate network delay
  }, [searchQuery, filters, page, rowsPerPage]);

  // Reset to first page when filters or search changes
  React.useEffect(() => {
    setPage(0);
  }, [searchQuery, filters]);

  // Setup mock refetch function for the parent component
  useEffect(() => {
    if (typeof setRefetchFn === "function") {
      setRefetchFn(() => () => {
        setIsLoading(true);
        setTimeout(() => {
          setFilteredData({
            firms: dummyOutstandingBalanceData,
            totalCount: dummyOutstandingBalanceData.length,
          });
          setIsLoading(false);
        }, 800);
      });
    }
  }, [setRefetchFn]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  // Filter visible columns
  const visibleColumnsList = defaultColumns.filter(
    column => visibleColumns[column.id],
  );

  // Format currency
  const formatCurrency = amount => {
    return `₹${amount.toLocaleString()}`;
  };

  return (
    <Paper
      sx={{
        width: "100%",
        overflow: "hidden",
        boxShadow: "none",
        border: "1px solid #E5E7EB",
        borderRadius: "0.5rem",
      }}>
      <TableContainer
        sx={{ maxHeight: "calc(100vh - 300px)" }}
        className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-50">
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {visibleColumnsList.map(column => (
                <TableCell
                  key={column.id}
                  align="center"
                  sx={{
                    minWidth: column.minWidth,
                    width: column.minWidth,
                    backgroundColor: "#F9FAFB",
                    fontWeight: "600",
                    fontSize: "14px",
                    color: "#374151",
                    borderBottom: "2px solid #E5E7EB",
                  }}
                  className="font-outfit">
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={visibleColumnsList.length} align="center">
                  <div className="flex items-center justify-center py-4">
                    <CircularProgress size={40} />
                  </div>
                </TableCell>
              </TableRow>
            ) : filteredData.firms.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumnsList.length} align="center">
                  <div className="py-4 text-gray-500">No records found</div>
                </TableCell>
              </TableRow>
            ) : (
              filteredData.firms
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map(row => (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={row.id}
                    sx={{
                      "&:hover": {
                        backgroundColor: "#F9FAFB",
                      },
                    }}>
                    {visibleColumnsList.map(column => {
                      const value = row[column.id];
                      return (
                        <TableCell
                          key={column.id}
                          align="center"
                          sx={{
                            padding: "12px 16px",
                            fontSize: "14px",
                            color: "#4B5563",
                          }}
                          className="font-outfit">
                          {column.id === "pendingAmount" ? (
                            formatCurrency(value)
                          ) : column.id === "status" ? (
                            <div
                              className={`px-3 py-1 rounded-full text-sm font-medium inline-block
                              ${
                                value === "Cleared"
                                  ? "bg-green-100 text-green-800"
                                  : value === "Overdue"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}>
                              {value}
                            </div>
                          ) : column.id === "action" ? (
                            <div className="flex items-center justify-center">
                              <NavLink
                                to={`/report/outstanding-balance/view/${row.id}`}
                                className="p-1.5 rounded-md bg-purple-50 hover:bg-purple-100 transition-colors">
                                <Eye size={16} className="text-[#5652B0]" />
                              </NavLink>
                            </div>
                          ) : (
                            value
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={filteredData.totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          borderTop: "1px solid #E5E7EB",
          ".MuiTablePagination-select": {
            borderRadius: "0.375rem",
          },
        }}
      />

      {isLoading && (
        <div className="absolute top-0 left-0 right-0">
          <div className="h-1 w-full overflow-hidden">
            <div className="animate-progress bg-[#5652B0] h-1"></div>
          </div>
        </div>
      )}
    </Paper>
  );
}

export { defaultColumns };
